/**
 * PolicyTable
 * Shared table wrapper with header styling for policy tables
 */

import React from 'react';
import { Spinner, Text, Checkbox } from '@fluentui/react-components';
import { TimeColumnHeader } from '../../common/DateTimeWithUTCInfo';
import { PolicyEmptyState } from './PolicyEmptyState';
import { PolicyTabType } from '../types';

interface ColumnConfig {
  key: string;
  label: string | React.ReactNode;
  width: string;
  align?: 'left' | 'center' | 'right';
  isTimeColumn?: boolean;
}

interface PolicyTableProps {
  /** Column configuration */
  columns: ColumnConfig[];
  /** Grid template columns CSS */
  gridTemplateColumns: string;
  /** Loading state */
  loading?: boolean;
  /** Loading message */
  loadingMessage?: string;
  /** Empty state for no results */
  isEmpty?: boolean;
  /** Selected tab for empty state message */
  selectedTab?: PolicyTabType;
  /** Custom empty message */
  emptyMessage?: string;
  /** Custom empty-state title (defaults to "No policies found") */
  emptyTitle?: string;
  /** Table rows content */
  children: React.ReactNode;
  /** Select all checkbox state (for Tenant view) */
  selectAllState?: {
    checked: boolean | 'mixed';
    onChange: (checked: boolean) => void;
  };
}

export function PolicyTable({
  columns,
  gridTemplateColumns,
  loading,
  loadingMessage = 'Loading policies...',
  isEmpty,
  selectedTab = 'DLP',
  emptyMessage,
  emptyTitle,
  children,
  selectAllState,
}: PolicyTableProps) {
  return (
    <div
      style={{
        border: '1px solid #e5e5e5',
        borderRadius: 8,
        overflow: 'hidden',
        background: 'white',
      }}
    >
      {/* Header row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns,
          fontWeight: 600,
          padding: '16px 20px',
          borderBottom: '1px solid #e5e5e5',
          background: '#fafafa',
          alignItems: 'center',
        }}
      >
        {columns.map((col, idx) => (
          <div
            key={col.key}
            style={{ textAlign: col.align || 'left' }}
          >
            {idx === 0 && selectAllState ? (
              <Checkbox
                checked={selectAllState.checked}
                onChange={(_, data) => selectAllState.onChange(!!data.checked)}
                aria-label="Select all policies"
                input={{ 'aria-label': 'Select all policies' }}
              />
            ) : col.isTimeColumn ? (
              <TimeColumnHeader>{col.label}</TimeColumnHeader>
            ) : (
              col.label
            )}
          </div>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 20px',
          }}
        >
          <Spinner size="medium" label={loadingMessage} />
        </div>
      ) : isEmpty ? (
        <PolicyEmptyState 
          selectedTab={selectedTab}
          message={emptyMessage}
          title={emptyTitle}
        />
      ) : (
        children
      )}
    </div>
  );
}

/**
 * Standard grid column configurations
 */
export const DASHBOARD_TABLE_COLUMNS: ColumnConfig[] = [
  { key: 'expand', label: '', width: '48px' },
  { key: 'name', label: 'Policy Name', width: 'minmax(250px,2.5fr)' },
  { key: 'priority', label: 'Priority', width: 'minmax(100px,0.8fr)' },
  { key: 'coverage', label: 'Tenant Coverage', width: 'minmax(180px,1.5fr)' },
  { key: 'lastModified', label: 'Last Modified', width: 'minmax(180px,1.5fr)', isTimeColumn: true },
  { key: 'actions', label: 'Actions', width: 'minmax(180px,1.5fr)', align: 'center' },
];

export const TENANT_TABLE_COLUMNS: ColumnConfig[] = [
  { key: 'select', label: '', width: '48px' },
  { key: 'name', label: 'Policy Name', width: 'minmax(250px,2.5fr)' },
  { key: 'priority', label: 'Priority', width: 'minmax(100px,0.8fr)' },
  { key: 'status', label: 'Status', width: 'minmax(140px,1.2fr)' },
  { key: 'lastDeployed', label: 'Last Deployed Time', width: 'minmax(180px,1.5fr)', isTimeColumn: true },
  { key: 'actions', label: 'Actions', width: 'minmax(120px,1fr)', align: 'center' },
];

export const DASHBOARD_GRID_COLUMNS = '48px minmax(250px,2.5fr) minmax(100px,0.8fr) minmax(180px,1.5fr) minmax(180px,1.5fr) minmax(180px,1.5fr)';
export const DASHBOARD_GRID_COLUMNS_NO_PRIORITY = '48px minmax(250px,2.5fr) minmax(180px,1.5fr) minmax(180px,1.5fr) minmax(180px,1.5fr)';
export const TENANT_GRID_COLUMNS = '48px minmax(250px,2.5fr) minmax(100px,0.8fr) minmax(140px,1.2fr) minmax(180px,1.5fr) minmax(120px,1fr)';
export const TENANT_GRID_COLUMNS_NO_PRIORITY = '48px minmax(250px,2.5fr) minmax(140px,1.2fr) minmax(180px,1.5fr) minmax(120px,1fr)';
